import { type EditorHost, WithDisposable } from '@blocksuite/block-std';
import { type AffineDragHandleWidget } from '@blocksuite/blocks';
import type { BlockModel, Text } from '@blocksuite/store';
import { seekTranscriptionAtTimestampSlot } from '@pique/core/components/blocksuite/block-suite-editor/specs/custom/pique/pique-slots';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

interface BlockWithTimestamps extends BlockModel {
  timestamps?: Text;
}

function isBlockWithTimestamps(block: BlockModel): block is BlockWithTimestamps {
  return block.flavour === 'affine:paragraph' || block.flavour === 'affine:list';
}

@customElement('timestamp-dock')
export class TimestampDock extends WithDisposable(LitElement) {
  static override styles = css`
    :host {
      position: relative;
    }

    .timestamp-dock {
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--affine-v2-layer-background-secondary);
      box-shadow: var(--affine-menu-shadow);
      border-radius: 8px;
      padding: 1px 4px;
      z-index: var(--affine-z-index-popover);
      position: absolute;
      border: 1px solid rgba(0, 0, 0, 0.05);
      transition: all 0.2s ease;
      font-size: 10px;
      font-weight: 500;
      color: var(--affine-text-secondary-color);
      min-width: 32px;
      height: 18px;
      text-align: center;
      letter-spacing: 0.3px;
      cursor: pointer;
    }

    .timestamp-dock:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transform: translateY(-1px);
    }

    /* Responsive positioning for small viewport width */
    @container viewport (width <= 640px) {
      .timestamp-dock {
        left: 22px !important; /* Increase left position on small screens */
      }
    }
  `;

  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor dragHandle!: AffineDragHandleWidget;

  @property({ attribute: false })
  accessor blockId: string = '';

  private getTimestamp(): string | null {
    if (!this.blockId || !this.host?.doc) return null;

    try {
      const block = this.host.doc.getBlockById(this.blockId);
      if (!block || !isBlockWithTimestamps(block)) return null;

      const timestampValue = block.timestamps?.toString();
      return timestampValue && timestampValue.length > 0 ? timestampValue : null;
    } catch {
      return null;
    }
  }

  private handleTimestampClick(e: Event) {
    e.stopPropagation();
    e.preventDefault();

    const timestamp = this.getTimestamp();
    if (timestamp) {
      // Emit the timestamp to seek to that position in the transcript
      seekTranscriptionAtTimestampSlot.emit(timestamp);
    }
  }

  override render() {
    if (!this.isConnected) return nothing;

    const timestamp = this.getTimestamp();
    if (!timestamp) return nothing;

    return html`
      <div
        class="timestamp-dock"
        @click=${this.handleTimestampClick}
        @mousedown=${(e: Event) => {
        e.stopPropagation();
        e.preventDefault();
      }}
        @mousemove=${(e: Event) => e.stopPropagation()}
      >
        ${timestamp}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'timestamp-dock': TimestampDock;
  }
}
